import { ComponentProps } from 'react';
import styled from 'styled-components';

import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import DashboardDatePicker from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import MultiSelect from 'dashboard/components-lib/MultiSelect';
import { MultiSelectPlaceholder } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import type {
  DateRangeFilterConfig,
  MultiSelectAltFilterConfig,
  MultiSelectFilterConfig,
  MultiSelectLegacyFilterConfig,
  SelectFilterConfig,
} from 'dashboard/components-lib/TableControls/types';

const ReactSelectFilter = ({
  filterConfig,
  ...props
}: {
  filterConfig: SelectFilterConfig | MultiSelectFilterConfig;
} & Omit<ComponentProps<typeof ReactSelect>, 'onChange' | 'options' | 'placeholder' | 'value'>) => {
  return (
    <ReactSelect
      styles={{
        container: () => ({
          width: 'auto',
        }),
        control: () => ({
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: 0,
        }),
        menu: () => ({
          width: '250px',
        }),
      }}
      onChange={filterConfig.onChange}
      options={filterConfig.options}
      isClearable={true}
      placeholder={filterConfig.placeholder}
      {...props}
      value={filterConfig.value}
      portal
    />
  );
};

export const SelectFilter = ({ filterConfig }: { filterConfig: SelectFilterConfig }) => {
  return <ReactSelectFilter filterConfig={filterConfig} />;
};

export const MultiSelectFilter = ({ filterConfig }: { filterConfig: MultiSelectFilterConfig }) => {
  return <ReactSelectFilter filterConfig={filterConfig} isMulti={true} />;
};

export const MultiSelectAltFilter = ({
  filterConfig,
}: {
  filterConfig: MultiSelectAltFilterConfig;
}) => {
  // Filter out props that are not needed for ReactMultiSelect
  const {
    id: _id,
    name: _name,
    enabled: _enabled,
    onFilterSelect: _onFilterSelect,
    group: _group,
    type: _type,
    htmlId: _htmlId,
    ...multiSelectConfig
  } = filterConfig;

  return (
    <ReactMultiSelect
      selectAllEnabled
      styles={{
        container: () => ({
          width: 'auto',
        }),
        control: () => ({
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          marginLeft: -1,
          minWidth: '120px',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
        menu: () => ({
          width: '250px',
        }),
      }}
      portal
      searchEnabled
      {...multiSelectConfig}
    />
  );
};

export const MultiSelectLegacyFilter = <SelectOptionValueType,>({
  filterConfig,
}: {
  filterConfig: MultiSelectLegacyFilterConfig<SelectOptionValueType>;
}) => {
  // Filter out props that are not needed for MultiSelect
  const {
    id: _id,
    name: _name,
    enabled: _enabled,
    onFilterSelect: _onFilterSelect,
    group: _group,
    type: _type,
    htmlId: _htmlId,
    ...multiSelectConfig
  } = filterConfig;

  return (
    <MultiSelect<SelectOptionValueType>
      css={{
        [MultiSelectPlaceholder]: {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: 0,
          maxWidth: '250px',
        },
      }}
      searchEnabled
      {...multiSelectConfig}
    />
  );
};

const DatePicker = styled(DashboardDatePicker)`
  div[role='group'] {
    > *:first-child {
      border-radius: 0;
    }

    &:not([aria-expanded='true']) {
      > *:first-child {
        border-left-color: transparent;
      }
    }
  }
`;

export const DateRangeFilter = ({
  filterConfig,
  ...props
}: {
  filterConfig: DateRangeFilterConfig;
}) => {
  return (
    <DatePicker
      handleDatesChange={filterConfig.onChange}
      dateRange={filterConfig.value}
      aria-label={filterConfig.name}
      showClear={false}
      {...filterConfig.datePickerProps}
      label={null}
      {...props}
    />
  );
};
